import { useEffect, useMemo, useRef } from "react";
import ImportantInfo from "../components/ImportantInfo";
import Paragraph from "../components/common/Paragraph";
import Title from "../components/common/Title";
import NewsArchive from "../components/news/NewsArchive";
import NewsEntry from "../components/news/NewsEntry";
import news, { newsArchive2025 } from "../config/news";
import { useTitle } from "../hooks/useTitle";

function getLocalDateString(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export default function News() {
  useTitle("News und Aktionen");

  const todayMarkerRef = useRef<HTMLDivElement>(null);
  const todayIndicatorIndex = useMemo(() => {
    const today = getLocalDateString(new Date());
    const nextEventIndex = news.findIndex(
      (newsItem) => (newsItem.sortDate ?? "") >= today
    );

    return nextEventIndex === -1 ? news.length : nextEventIndex;
  }, []);

  useEffect(() => {
    if (!todayMarkerRef.current || news.length === 0) {
      return;
    }

    const timeout = window.setTimeout(() => {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      todayMarkerRef.current?.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "center",
      });
    }, 700);

    return () => window.clearTimeout(timeout);
  }, []);

  const todayIndicator = (
    <div
      ref={todayMarkerRef}
      className="scroll-mt-24 flex items-center gap-4"
      aria-label="Heute"
    >
      <div className="h-px flex-1 bg-red-200" />
      <div className="rounded-full bg-red-700 px-5 py-2 text-sm md:text-base font-bold text-white shadow-md">
        Heute
      </div>
      <div className="h-px flex-1 bg-red-200" />
    </div>
  );

  return (
    <>
      <section id={"news"} className={"py-12 lg:py-24"}>
        <Title>Unsere News & Aktionen</Title>

        {news.length <= 0 ? (
          <Paragraph
            data-aos={"fade-right"}
            data-aos-duration={600}
            data-aos-delay={100}
          >
            Zur Zeit gibt es keine News oder Aktionen
          </Paragraph>
        ) : null}
        <div
          className={
            "flex flex-col mt-5 lg:mt-24 md:gap-16 px-4 lg:px-24 gap-12"
          }
        >
          {news.map((newsItem, index) => {
            return (
              <div
                className="contents"
                key={`${newsItem.sortDate}-${newsItem.title}`}
              >
                {index === todayIndicatorIndex ? todayIndicator : null}
                <NewsEntry news={newsItem} index={index} />
              </div>
            );
          })}
          {news.length > 0 && todayIndicatorIndex === news.length
            ? todayIndicator
            : null}
        </div>

        <div className="mt-8 lg:mt-16 px-4 lg:px-24">
          <NewsArchive year="2025" items={newsArchive2025} />
        </div>
      </section>

      <div className={"mb-4 lg:mb-24"}>
        <ImportantInfo />
      </div>
    </>
  );
}
